import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useSEO } from '../utils/seo';
import './LegalPage.css';

const LAST_UPDATED = '6 March 2026';
const COMPANY = 'JSDEVBRAINS Digital Private Limited';

const SECTIONS = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: `By accessing or using DevBrains (the "Platform") — including the website devbrains.in, mobile applications, and any related services — you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, please do not use the Platform.

These Terms constitute a legally binding agreement between you and ${COMPANY} ("DevBrains", "we", "us", or "our"), a company incorporated under the laws of India with its registered office in Bengaluru, Karnataka.

We reserve the right to modify these Terms at any time. We will provide notice of significant changes by updating the "Last Updated" date and, where appropriate, notifying you by email. Your continued use of the Platform after changes constitutes acceptance of the updated Terms.`,
  },
  {
    id: 'accounts',
    title: '2. User Accounts & Registration',
    content: `2.1 You must be at least 18 years old to create an account on DevBrains.

2.2 You agree to provide accurate, current, and complete information during registration and to keep this information up to date.

2.3 You are responsible for maintaining the confidentiality of your account credentials. You agree to notify us immediately at support@devbrains.in if you suspect any unauthorised access to your account.

2.4 You may not create multiple accounts, share your account with others, or use another person's account without their explicit permission.

2.5 We reserve the right to suspend or terminate accounts that violate these Terms, engage in fraudulent activity, or cause harm to other users.

2.6 User roles: DevBrains has three user roles — Mentee, Mentor, and Admin. Your role determines which features you can access. Mentors must pass our verification process (see Section 6) before they can accept bookings.`,
  },
  {
    id: 'services',
    title: '3. Platform Services',
    content: `3.1 DevBrains provides a marketplace connecting mentees with professional technology mentors for one-to-one sessions, group sessions, code reviews, mock interviews, and project-based certification programs.

3.2 DevBrains acts as an intermediary platform. We do not directly employ mentors, and mentors are independent professionals, not employees or agents of DevBrains.

3.3 Free Session: Each new mentee account is entitled to one free 30-minute introductory session with any mentor. This credit is non-transferable and expires 90 days after account creation.

3.4 DevBrains reserves the right to modify, suspend, or discontinue any feature or service at any time, with reasonable notice where possible.

3.5 Certifications issued by DevBrains are based on project completion, code review, and a technical interview. DevBrains does not guarantee employment outcomes from any certification.`,
  },
  {
    id: 'payments',
    title: '4. Payments & Refunds',
    content: `4.1 All payments are processed through Razorpay, a licensed payment aggregator regulated by the Reserve Bank of India (RBI). We do not store your payment card information.

4.2 Prices on mentor profiles are displayed inclusive of the mentor's fee. GST at 18% is added at checkout as applicable under Indian tax law.

4.3 Escrow: Payments are held in escrow upon booking and released to the mentor's wallet only after session completion. DevBrains retains a 15% platform commission from each transaction.

4.4 Refund Policy:
• Cancellation more than 24 hours before session: 100% refund
• Cancellation 2–24 hours before session: 50% refund
• Cancellation less than 2 hours before session: No refund
• Mentor no-show (mentor fails to join within 15 minutes): 100% full refund + rebooking credit
• Technical failure on DevBrains' infrastructure preventing the session: 100% refund

4.5 Refunds are processed to the original payment method within 5–7 business days (UPI: 1–3 days).

4.6 TDS: DevBrains deducts TDS at 10% on mentor earnings above ₹15,000 per financial year as required under Section 194H of the Income Tax Act, 1961. TDS certificates (Form 16A) are issued quarterly.`,
  },
  {
    id: 'conduct',
    title: '5. User Conduct & Prohibited Activities',
    content: `5.1 You agree not to use the Platform to:
• Harass, abuse, threaten, or intimidate other users
• Share false, misleading, or defamatory content
• Circumvent DevBrains' platform by arranging sessions or payments outside the Platform to avoid commissions
• Share session recordings without the explicit written consent of all participants
• Upload malicious code, viruses, or any software designed to disrupt the Platform
• Scrape, crawl, or automatically extract data from the Platform
• Impersonate another person or create fake profiles
• Violate any applicable law, regulation, or third-party rights

5.2 Off-Platform Transactions: Arranging paid mentoring sessions with DevBrains mentors outside the Platform is a violation of these Terms and may result in immediate account termination for both parties.

5.3 Intellectual Property: Session content, course materials, and resources shared by mentors remain the intellectual property of the respective mentor. You may not reproduce, distribute, or resell any materials shared during sessions without the mentor's written consent.`,
  },
  {
    id: 'mentors',
    title: '6. Mentor Standards & Verification',
    content: `6.1 All mentors on DevBrains must pass a mandatory verification process including identity verification, professional credential validation, and a technical screening interview.

6.2 Mentors must maintain a minimum rating of 3.5★ to remain active on the Platform. Accounts falling below this threshold may be reviewed and temporarily suspended.

6.3 Mentors are responsible for the accuracy of the information on their profiles. False credentials or misrepresentation will result in immediate permanent suspension.

6.4 Mentor No-Show Policy:
• 1st no-show: Warning issued
• 2nd no-show: Profile hidden from search for 7 days
• 3rd no-show: Account suspended

6.5 Mentors are independent contractors, not employees of DevBrains. Mentors are responsible for their own tax obligations, including GST registration where applicable.

6.6 Mentors may not use DevBrains' branding, logos, or trademarks without express written permission.`,
  },
  {
    id: 'ip',
    title: '7. Intellectual Property',
    content: `7.1 DevBrains and its licensors own all rights in the Platform, including the website, software, design, logos, and original content. Nothing in these Terms grants you any right in DevBrains' intellectual property.

7.2 User Content: By posting content on DevBrains (reviews, profile information, session notes), you grant DevBrains a worldwide, royalty-free, non-exclusive licence to use, display, and distribute such content for the purpose of operating and promoting the Platform.

7.3 Certificates: DevBrains project-based certificates are issued to the individual mentee only. They are non-transferable. Certificates are blockchain-verified and any attempt to falsify or forge a certificate is a criminal offence under applicable law.

7.4 Feedback: If you provide feedback or suggestions about the Platform, DevBrains may use this feedback without any obligation to compensate you.`,
  },
  {
    id: 'privacy',
    title: '8. Privacy',
    content: `Your privacy is important to us. Please review our Privacy Policy (available at devbrains.in/privacy) which explains how we collect, use, and protect your personal information. By using the Platform, you consent to the data practices described in the Privacy Policy.`,
  },
  {
    id: 'disclaimers',
    title: '9. Disclaimers & Limitation of Liability',
    content: `9.1 The Platform is provided "as is" and "as available" without warranties of any kind, either express or implied.

9.2 DevBrains does not guarantee the quality, accuracy, or fitness for purpose of any session, mentor advice, or certification outcome. Mentors are independent professionals and DevBrains is not responsible for the content of sessions.

9.3 To the maximum extent permitted by law, DevBrains' total liability to you for any claim arising from or related to the Platform is limited to the amount you paid to DevBrains in the 3 months immediately preceding the event giving rise to the claim.

9.4 DevBrains is not liable for indirect, consequential, punitive, or special damages, including loss of profits or career opportunities.

9.5 DevBrains is not responsible for service interruptions caused by third-party services (including Razorpay, Zoom, Google Meet, or Microsoft Teams).`,
  },
  {
    id: 'termination',
    title: '10. Termination',
    content: `10.1 You may close your account at any time from Dashboard → Settings → Delete Account.

10.2 DevBrains may suspend or terminate your account immediately if you breach these Terms, engage in fraudulent activity, or cause harm to other users or the Platform.

10.3 Upon termination:
• Pending session payments are refunded according to the cancellation policy
• Mentor wallet balances above ₹500 are paid out within 14 business days
• Your personal data is handled as described in our Privacy Policy
• Certificates you have earned remain valid and verifiable

10.4 Data export: Before deleting your account, you can export all your data (sessions, ratings, certificates, invoices) from Dashboard → Settings → Export Data.`,
  },
  {
    id: 'governing',
    title: '11. Governing Law & Dispute Resolution',
    content: `11.1 These Terms are governed by the laws of India, without regard to conflict of law principles.

11.2 For disputes arising from these Terms, both parties agree to first attempt resolution through good-faith negotiation. If unresolved within 30 days, disputes shall be referred to arbitration under the Arbitration and Conciliation Act, 1996, with the seat of arbitration in Bengaluru, Karnataka.

11.3 Nothing in this clause prevents either party from seeking urgent injunctive relief from a court of competent jurisdiction.

11.4 The courts of Bengaluru, Karnataka shall have exclusive jurisdiction over any matter that cannot be resolved through arbitration.

11.5 Consumer disputes: Nothing in these Terms affects your rights as a consumer under applicable Indian consumer protection laws.`,
  },
  {
    id: 'contact',
    title: '12. Contact',
    content: `For questions about these Terms, please contact:

JSDEVBRAINS Digital Private Limited
Legal Team
Email: legal@devbrains.in
Address: Bengaluru, Karnataka, India – 560001

For general support: support@devbrains.in
For grievance redressal: grievance@devbrains.in (as required under IT (Intermediary Guidelines) Rules, 2021)

Grievance Officer: Available from Dashboard → Help → Contact Support`,
  },
];

export default function TermsPage({ onSignIn }) {
  const [active, setActive] = useState('acceptance');
  useSEO({
    title: 'Terms & Conditions',
    description: 'Read the DevBrains Terms and Conditions — governing your use of the platform, payments, mentor standards, and your rights.',
    canonical: '/terms',
    noIndex: false,
  });

  return (
    <>
      <Navbar onSignIn={onSignIn} />
      <div className="legal-page">

        {/* Hero */}
        <div className="legal-hero">
          <div className="legal-hero-inner">
            <div className="legal-hero-badge">📋 Legal</div>
            <h1 className="legal-hero-title">Terms &amp; Conditions</h1>
            <p className="legal-hero-sub">
              Last updated: <strong>{LAST_UPDATED}</strong> &nbsp;·&nbsp;
              Effective for all DevBrains users worldwide
            </p>
            <div className="legal-hero-chips">
              <span>🏢 {COMPANY}</span>
              <span>🇮🇳 Bengaluru, India</span>
              <span>⚖️ Governed by Indian Law</span>
            </div>
            <div className="legal-quick-nav">
              <Link to="/privacy" className="legal-quick-link">📄 Privacy Policy →</Link>
              <Link to="/blog" className="legal-quick-link">❓ Help Centre →</Link>
            </div>
          </div>
        </div>

        <div className="legal-layout">
          {/* Sticky sidebar TOC */}
          <aside className="legal-toc">
            <div className="legal-toc-label">Contents</div>
            {SECTIONS.map(s => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className={`legal-toc-item ${active === s.id ? 'active' : ''}`}
                onClick={() => setActive(s.id)}
              >
                {s.title}
              </a>
            ))}
          </aside>

          {/* Content */}
          <div className="legal-content">
            <div className="legal-notice">
              <span className="legal-notice-icon">ℹ️</span>
              <span>Please read these Terms carefully before using DevBrains. By creating an account or using the platform, you agree to be bound by these Terms.</span>
            </div>

            {SECTIONS.map(s => (
              <section key={s.id} id={s.id} className="legal-section">
                <h2 className="legal-section-title">{s.title}</h2>
                {s.content.split('\n\n').map((para, i) => (
                  <p key={i} className="legal-para">{para}</p>
                ))}
              </section>
            ))}

            <div className="legal-footer-note">
              <p>These Terms were last updated on <strong>{LAST_UPDATED}</strong>. If you have questions, contact <a href="mailto:legal@devbrains.in">legal@devbrains.in</a>.</p>
              <div className="legal-also">
                <Link to="/privacy">Privacy Policy →</Link>
                <Link to="/blog">Help Centre →</Link>
                <Link to="/mentors">Find a Mentor →</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
